import React, { useEffect, useState, useMemo } from "react";
import { blockchainService } from "../../../../services/blockchainService";
import TopicDocuments from "./Topic/TopicDocuments";
import { iniciarEtapa } from "../Actions/IniciarEtapa";
import ProgressBar from '../ProgressBar/ProgressBar';
import ProgressBarFilho from '../ProgressBarFilho/ProgressBarFilho';
import ProjectContract from "../ProjectContract/ProjectContract";
import { TopicMergeSection } from "./Topic/TopicMergeSection";
import {
  TopicsContainer,
  ScreenTitle,
  TopicList,
  TopicCard,
  TopicInfo,
  TopicIndex,
  TopicText,
  TopicTitle,
  TopicArrow,
  TopActions,
  StartButton,
  StyledSpan
} from "./DocumentModelTopics.styles";

const DocumentModelTopics = ({ modeloId,
  projectId,
  telaName,
  onDocumentsConfirmed, }) => {
  const token = sessionStorage.getItem("Authorization");
  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    }),
    [token]
  );

  const [model, setModel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [docConfirmed, setDocConfirmed] = useState(false);
  const [startedTopics, setStartedTopics] = useState(new Set());
  const [contractAddress, setContractAddress] = useState("");

  const [progress, setProgress] = useState({
    global: { total: 0, confirmados: 0, percentage: 0 },
    topics: {},
  });
  const [questoesPorTopico, setQuestoesPorTopico] = useState({});

  const isModeloAdministrativo =
    telaName?.toLowerCase().includes("administrativo");


  const loadModel = async () => {
    try {
      const data = await blockchainService.getDocumentModelTopics(modeloId, projectId, headers);
      setModel(data);

      const topicIds = Object.values(data.topicos).map(t => t.titulo.id);
      const startedSet = new Set();

      await Promise.all(
        topicIds.map(async (topicId) => {
          const iniciado = await blockchainService.checkTopicStarted(
            projectId,
            topicId,
            headers
          );
          if (iniciado) startedSet.add(topicId);
        })
      );

      setStartedTopics(startedSet);
    } catch (error) {
      console.error("Erro ao carregar modelo:", error);
    } finally {
      setLoading(false);
    }
  };

  /* ✅ Verifica se todos os documentos obrigatórios estão confirmados */
  const verificaDocsConfirmados = async () => {
    try {
      const response =
        await blockchainService.getConfirmedDocumentsCount(
          projectId,
          headers
        );

      const { requirements_met, total_confirmed } =
        response?.data || response || {};

      const allRequirementsMet =
        requirements_met?.comercial &&
        requirements_met?.juridico &&
        requirements_met?.engenharia &&
        total_confirmed >= 9;

      setDocConfirmed(allRequirementsMet);

      // adiciona isso
      onDocumentsConfirmed?.(allRequirementsMet);

      return allRequirementsMet;
    } catch (error) {
      console.error(
        "❌ Erro ao verificar documentos confirmados:",
        error
      );
      setDocConfirmed(false);
      return false;
    }
  };

  useEffect(() => {
    if (!model?.topics) return;

    const total = model.topics.length;

    const confirmados = model.topics.filter(
      (doc) => doc.confirmado === true
    ).length;

    setProgress({
      global: {
        total,
        confirmados,
      },
    });
  }, [model]);

  const loadProgress = async () => {
    try {
      const data = await blockchainService.getDocumentModelProgress(
        modeloId,
        projectId,
        headers
      );

      setProgress(data);
    } catch (error) {
      console.error("Erro ao carregar progresso:", error);
    }
  };

  /* 🚀 Inicialização */
  useEffect(() => {
    loadModel();
    verificaDocsConfirmados();
    loadProgress();
  }, [modeloId, projectId, headers]);



  if (loading) return <div>Carregando modelo {telaName}...</div>;
  if (!model) return <div>Modelo não encontrado</div>;

  return (
    <TopicsContainer>
      <TopActions>
        <ProjectContract
          projectId={projectId}
          modeloId={modeloId}
          telaName={telaName}
          onDocumentsConfirmed={setDocConfirmed}
          onContractLoaded={setContractAddress}
        />
      </TopActions>
      <ScreenTitle>
        <StyledSpan>{telaName}</StyledSpan>
      </ScreenTitle>
      {/* 📊 Barra de progresso do modelo */}
      <ProgressBar
        progress={progress?.global ?? { total: 0, confirmados: 0 }}
      />

      <TopicList>
        {model.topicos.map((topico) => {
          const topicId = topico.titulo.id;
          const isActive = selectedTopic?.titulo.id === topicId;
          const isStarted = startedTopics.has(topicId);

          // ✅ ISSO PODE, porque está dentro do map (JS)
          const podeIniciarEtapa =
            docConfirmed ||
            (!isModeloAdministrativo && topico.titulo.topico === "01");
          return (
            <React.Fragment key={topicId}>
              <TopicCard
                onClick={() => setSelectedTopic(isActive ? null : topico)}
                $active={isActive}
              >
                <TopicInfo>
                  <TopicArrow>{isActive ? "⌄" : "›"}</TopicArrow>
                  <TopicIndex>{topico.titulo.topico}</TopicIndex>

                  <ProgressBarFilho
                    progress={
                      progress?.topics?.[String(topicId)] ?? {
                        total: 0,
                        confirmados: 0,
                        percentage: 0,
                      }
                    }
                  />
                  <TopicText>
                    <TopicTitle>{topico.titulo.label}</TopicTitle>
                  </TopicText>
                </TopicInfo>


                {/* 🟢 BOTÃO INICIAR ETAPA */}
                {!isStarted && podeIniciarEtapa ? (
                  <StartButton
                    $started={false}
                    onClick={async (e) => {
                      e.stopPropagation();
                      await iniciarEtapa(
                        topicId,
                        projectId,
                        headers,
                        model.id,
                        async () => {
                          setStartedTopics(prev => new Set(prev).add(topicId));
                        }
                      );
                    }}
                  >
                    Iniciar etapa
                  </StartButton>
                ) : isStarted ? (
                  <StartButton $started={true}>
                    Etapa iniciada
                  </StartButton>
                ) : null}
              </TopicCard>

              {/* 🔽 Itens do tópico */}
              {isActive && (
                <>
                  <TopicDocuments
                    projectId={projectId}
                    documentModel={model}
                    topico={topico.titulo}
                    headers={headers}
                    onConfirmarTopico={async () => {
                      await verificaDocsConfirmados();
                      await loadModel();
                    }}
                    onLoadQuestoes={(questoes) => {
                      setQuestoesPorTopico(prev => ({
                        ...prev,
                        [topico.titulo.id]: questoes
                      }));
                    }}
                  />

                  {(questoesPorTopico[topico.titulo.id]?.length || 0) > 1 && (
                    <TopicMergeSection
                      projectId={projectId}
                      modeloId={modeloId}
                      topicData={{
                        titulo: topico.titulo,
                        questoes: questoesPorTopico[topico.titulo.id] || []
                      }}
                      contractAddress={contractAddress}
                      headers={headers}
                    />
                  )}

                </>
              )}
            </React.Fragment>
          );
        })}
      </TopicList>
    </TopicsContainer>
  );
};

export default DocumentModelTopics;
